import React, { useEffect } from 'react';
import { prop } from 'ramda';
import { useOAPISocket } from 'server';
import { Helmet } from 'react-helmet-async';
import { useGetTags } from 'js/hooks/useGetTags';
import { usePas, useAutoLogin } from 'js/hooks/auth';
import { usePublicApi } from 'js/hooks/usePublicAPI';
import { TagManager } from 'js/components/system/TagManager';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { PopupManager } from 'js/components/common/PopupManager';
import { useAppSelector, useAuthSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import {
    useClientIP,
    useDetectLanguage,
    useDetectUserDetails,
    useGeoIP,
    useDetectCurrency,
    useAdminRole,
    useUserKeepAlive
} from 'js/hooks/user';

import * as hooks from './hooks';
import { AppPostInit } from './components/AppPostInit';

import style from './App.scss';

export const App = ({ children }) => {
    const htmlBaseClasses = useDeviceSelector(prop('htmlBaseClasses'));
    const togglePreloader = hooks.useTogglePreloader();
    const fullLang = useUserSelector(prop('lang'))?.replace('_', '-');
    const [{ isLoading: isAppLoading, app: appData, error }] = hooks.useAppInitialResources();
    const [{ isLoading: isTagsLoading }] = useGetTags();
    const skipAutoLoginDependency = hooks.useSkipAutoLoginDependency();
    const isSportsbookRootURLReady = useAppSelector(prop('isSportsbookRootURLReady'));
    const wasAutoLoginCompleted = useAuthSelector(prop('wasAutoLoginCompleted'));
    const isAppReady = !isAppLoading && isSportsbookRootURLReady && (wasAutoLoginCompleted || skipAutoLoginDependency);
    const isTagsReady = appData && !isTagsLoading && (wasAutoLoginCompleted || skipAutoLoginDependency);

    usePas();

    useOAPISocket();
    useGeoIP();
    useClientIP();
    hooks.useDeviceIdFromNative();
    useUserKeepAlive();
    hooks.usePushNotifier();
    hooks.useNotificationSubscriptions();

    useAutoLogin();

    useAdminRole();
    usePublicApi();
    hooks.useHotKeys();
    useDetectLanguage();
    useDetectCurrency();
    useDetectUserDetails();
    hooks.useAppNotifier();
    hooks.useEnableLogger();
    hooks.useDraftThemeCookie();
    hooks.useGetMenuItems();
    hooks.useRefreshSafari();
    hooks.useSportsbookIntegration();
    hooks.useGamesInitialResources();
    hooks.useOnDeviceChange();

    useEffect(() => {
        togglePreloader(Boolean(isAppReady && isTagsReady));
    }, [isAppReady, isTagsReady, togglePreloader]);

    if (error) {
        const errorMessage = typeof error === 'string' || error instanceof window.Error
            ? error.toString()
            : 'playtech.mobile.system.error.no.response';

        return <ErrorMessage className={style.error} message={errorMessage} />;
    }

    return (
        <>
            {isAppReady && (
                <>
                    <Helmet>
                        <html lang={fullLang} className={htmlBaseClasses} />
                    </Helmet>
                    <AppPostInit />
                    {children}
                    <PopupManager />
                </>
            )}
            {isTagsReady && <TagManager />}
        </>
    );
};
